import { AuthApiService, Claims, NavigationService, OidcService } from '@revenuewell/front-end-bundle';
import { ApptRequestVerificationSummary } from './types/appt-request-verification-summary';
import CarrierSummary from './types/carrier-name';
import { KaynDashboardUrlResponse } from './types/kayn-dashboard-url-response';
import { PatientVerificationSummary } from './types/patient-verification-summary';
import { PracticeOnboarding } from './types/practice-onboarding';
import { Provider } from './types/provider';
import { VerificationPriority } from './types/verification-priority';
import { VerificationResponse } from './types/verification-response';
import { VerificationSummariesRequest } from './types/verification-summaries-request';
import { VerificationSummary } from './types/verification-summary';

export default class InsuranceV2Service extends AuthApiService {
  private config: any;

  private baseUrl: string;

  private claims: Claims | null | undefined;

  constructor(config: any, oidcService: OidcService) {
    super(oidcService, new NavigationService());
    this.config = config;
    this.baseUrl = this.config?.insurance2?.apiUrl;
    this.loadClaims();
  }

  loadClaims = async () => {
    if (this.claims) return;

    const claims = await this.oidcService.getClaims();

    this.claims = claims;
  }

  getCarrierSummaries = async (locationId: number): Promise<CarrierSummary[]> => {
    const url = `${this.baseUrl}/practice/${locationId}/carrier-summaries`;
    const response = await this.authFetch(url);

    return response.json();
  }

  getPracticeOnboarding = async (locationId: number): Promise<PracticeOnboarding> => {
    await this.loadClaims();
    const isCustomerSupportUser = this.claims?.firstName == 'Customer' && this.claims?.lastName == 'Support';
    const url = `${this.baseUrl}/practice/${locationId}/onboarding`;
    const response = await this.authFetch(url);
    const resJson: PracticeOnboarding = await response.json();

    resJson.isInsuranceVerificationDisplayActive = isCustomerSupportUser || resJson.isInsuranceVerificationDisplayActive

    return resJson;
  }

  getProviders = async (locationId: number): Promise<Provider[]> => {
    const url = `${this.baseUrl}/practice/${locationId}/providers`;
    const response = await this.authFetch(url);

    return response.json();
  }

  runVerification = async (locationId: number, idPatient: number, idProvider: number,
    verificationPriority: VerificationPriority, idCarrierName?: number): Promise<void> => {
    const url = `${this.baseUrl}/practice/${locationId}/verification/run`;

    await this.authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idPatient, idProvider, verificationPriority, idCarrierName })
    });
  }

  selfPayVerification = async (locationId: number, idPatient: number, idProvider: number,
    verificationPriority: VerificationPriority): Promise<void> => {
    const url = `${this.baseUrl}/practice/${locationId}/verification/self-pay`;

    await this.authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idPatient, idProvider, verificationPriority })
    });
  }

  selfPayApptRequest = async (appointmentRequestId: number, locationId: number, providerId: number): Promise<void> => {
    const url = `${this.baseUrl}/practice/${locationId}/verification/appointment-request/self-pay?appointmentRequestId=${appointmentRequestId}&providerId=${providerId}`;

    await this.authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  manualVerification = async (locationId: number, idPatient: number, idProvider: number,
    verificationPriority: VerificationPriority): Promise<void> => {
    const url = `${this.baseUrl}/practice/${locationId}/verification/manual`;

    await this.authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ idPatient, idProvider, verificationPriority })
    });
  }

  manuallyVerifyAppointmentRequest = async (
    appointmentRequestId: number, locationId: number, providerId: number): Promise<void> => {
    const url = `${this.baseUrl}/practice/${locationId}/verification/appointment-request/manually-verify?appointmentRequestId=${appointmentRequestId}&providerId=${providerId}`;

    await this.authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
  }

  getVerificationSummaries = async (locationId: number, request: VerificationSummariesRequest):
    Promise<VerificationSummary[]> => {
    const url = `${this.baseUrl}/practice/${locationId}/verification/summaries`;

    const response = await this.authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request)
    });

    return await response.json();
  }

  getPatientVerificationSummaries = async (patientIds: number[], locationId: number, providerId?: number):
    Promise<PatientVerificationSummary[]> => {
    let url = `${this.baseUrl}/practice/${locationId}/verification/summaries?ids=${patientIds.join(',')}&locationId=${locationId}`;

    if (providerId) url += `&providerId=${providerId}`;

    const response = await this.authFetch(url);

    return await response.json();
  }

  getAppointmentRequestVerificationSummaries = async (appointmentRequestIds: number[], locationId: number):
    Promise<ApptRequestVerificationSummary[]> => {
    const url = `${this.baseUrl}/practice/${locationId}/verification/appointment-request/summaries?&locationId=${locationId}`;

    const response = await this.authFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        appointmentRequestIds
      })
    });

    return await response.json();
  }

  getAppointmentRequestVerificationSummary = async (appointmentRequestId: number, locationId: number):
    Promise<ApptRequestVerificationSummary> => {
    const url = `${this.baseUrl}/practice/${locationId}/verification/appointment-request/${appointmentRequestId}/summary?locationId=${locationId}`;

    const response = await this.authFetch(url);

    return await response.json();
  }

  getVerificationSummary = async (locationId: number, verificationRequestId: number): Promise<VerificationSummary> => {
    const url = `${this.baseUrl}/practice/${locationId}/verification/${verificationRequestId}/summary`;
    const response = await this.authFetch(url);

    return await response.json();
  }

  getVerificationDocumentUrl = async (locationId: number, verificationRequestId: number): Promise<string> => {
    const url = `${this.baseUrl}/practice/${locationId}/verification/${verificationRequestId}/response/document-url`;
    const response = await this.authFetch(url);

    return (await response.json())?.downloadUrl;
  }

  getVerificationResponse = async (locationId: number, verificationRequestId: number):
    Promise<VerificationResponse> => {
    const url = `${this.baseUrl}/practice/${locationId}/verification/${verificationRequestId}/response`;
    const response = await this.authFetch(url);

    return await response.json();
  }

  getKaynDahsboardUrl = async (locationId: number): Promise<KaynDashboardUrlResponse> => {
    const url = `${this.baseUrl}/kayn/${locationId}/dashboard-url`;
    const response = await this.authFetch(url);
    return await response.json();
  }
}